import React, { useRef, useCallback, useMemo, useContext, useState } from 'react';
import {
    getBoundingTopSafari,
    usePopupListeners,
    useWindowDimensionsHeight
} from '../../../../../../common/utils/windowDimensions';
import css from './DropdownMenu.module.scss';
import FormCheckbox from '../../../form-checkbox/FormCheckbox/FormCheckbox';
import classNames from 'classnames';
import T, { t } from '../../../../../../common/components/T';
import { checkBrowser, declOfNum, getWithZoom } from '../../../../../../common/utils/utils';
import OptionTitle from '../OptionTitle/OptionTitle';
import { getIdetify, validValue } from '../../helpers';
import { getMemoizedData } from '../../../../../../common/utils/memo';
import UserContext from '../../../../../../common/utils/getContext';
import CircleLoader from '../../../../../plug/CircleLoader';
import { generateCompStylers } from '../../../../../../common/utils/styles';
import FormInput from '../../../form-input/FormInput/FormInput';

const DropdownMenu = ({
    optionsList,
    nativeOptionsList,
    searchValue,
    loading,
    pickerElement,
    handleClose,
    getOptions,
    limit,
    multiple,
    attribute,
    val,
    selectHandler,
    transl,
    formatter,
    maxMenuHeight = 273,
    setOptionsList,
    nextPageLink,
    insertComponentInOptionBefore = null,
    compStyle,
    selectAll,
    allSelected,
    customUpdatePosition,
    menuAlign,
    dropdownSearch
}) => {
    const { stylers, classes } = generateCompStylers(compStyle);

    const [pagerLoader, setPagerLoader] = useState(false);

    const modalElement = useRef();
    const heightDimensions = useWindowDimensionsHeight();

    const func = useCallback(() => {
        if (modalElement.current && pickerElement.current) {
            const picker = pickerElement.current.getBoundingClientRect();
            const modal = modalElement.current.getBoundingClientRect();

            // TODO ZOOM

            const top = checkBrowser(getBoundingTopSafari(pickerElement.current));

            // TODO ZOOM

            const scrollValue = getWithZoom(window?.scrollY);

            let tPos = top + scrollValue + checkBrowser(picker.height);

            let maxHeightValue = maxMenuHeight;

            const upSpace = heightDimensions - (heightDimensions - top);
            const downSpace = heightDimensions - (top + picker.height);

            if (downSpace < modal.height + 300 && downSpace < maxMenuHeight) {
                if (checkBrowser(upSpace) > downSpace && checkBrowser(upSpace) > 100) {
                    if (upSpace >= maxHeightValue || modal.height < maxMenuHeight) {
                        tPos = top + scrollValue - checkBrowser(modal.height);
                    } else {
                        tPos = top + scrollValue - checkBrowser(upSpace);
                        maxHeightValue = checkBrowser(upSpace);
                    }
                } else if (downSpace > 300) {
                    maxHeightValue = downSpace;
                }
            }

            if (menuAlign) {
                modalElement.current.style.minWidth = checkBrowser(picker.width) + 'px';
                if (menuAlign === 'center') {
                    modalElement.current.style.left = checkBrowser(picker.left + picker.width) / 2 + 'px';
                } else if (menuAlign === 'left') {
                    modalElement.current.style.left = checkBrowser(picker.left) + 'px';
                } else if (menuAlign === 'right') {
                    modalElement.current.style.left = picker.left + picker.width + 'px';
                }
            } else {
                modalElement.current.style.left = checkBrowser(picker.left) + 'px';
                modalElement.current.style.width = checkBrowser(picker.width) + 'px';
            }

            modalElement.current.style.top = tPos + 'px';
            modalElement.current.style.maxHeight = maxHeightValue ? maxHeightValue + 'px' : 'none';
        }
    }, [pickerElement, menuAlign, heightDimensions, maxMenuHeight, customUpdatePosition]);

    const [dropdownSearchValue, setDropdownSearchValue] = useState('');
    const optionsListFiltered = useMemo(() => {
        if (dropdownSearch && dropdownSearchValue?.length) {
            const getKeysToSearch = (opt) =>
                Object.keys(opt).filter(
                    (key) =>
                        typeof opt[key] === 'string' &&
                        (getOptions?.fieldsToSearch ? getOptions?.fieldsToSearch.includes(key) : true)
                );
            const findedBegin = optionsList.filter((opt) => {
                return getKeysToSearch(opt).find((key) => {
                    return opt[key].toLowerCase().startsWith(dropdownSearchValue.toLowerCase());
                });
            });
            const findedOther = optionsList.filter((opt) => {
                return getKeysToSearch(opt).find((key) => {
                    return (
                        opt[key].toLowerCase().includes(dropdownSearchValue.toLowerCase()) &&
                        !opt[key].toLowerCase().startsWith(dropdownSearchValue.toLowerCase())
                    );
                });
            });
            return [...(findedBegin || []), ...(findedOther || [])];
        }
        return multiple || searchValue?.length
            ? optionsList
            : optionsList?.filter((opt) => {
                  const isSelected = validValue(multiple, val, opt);
                  return !isSelected;
              });
    }, [getOptions?.fieldsToSearch, dropdownSearch, dropdownSearchValue, multiple, optionsList, searchValue?.length, val]);
    const { userToken, lang } = useContext(UserContext);

    const updatePosition = useMemo(
        () => (!loading && searchValue?.length && optionsListFiltered?.length) || val?.length,
        [loading, optionsListFiltered?.length, searchValue?.length, val]
    );
    usePopupListeners(pickerElement, func, modalElement, handleClose, false, updatePosition);

    return (
        <>
            <div
                onScroll={(e) => {
                    var scrollTop = Math.ceil(e.target.scrollTop);
                    var scrollHeight = Math.ceil(e.target.scrollHeight);
                    var clientHeight = Math.ceil(e.target.clientHeight);

                    if (scrollTop + clientHeight + 2 >= scrollHeight) {
                        if (nextPageLink?.current && !pagerLoader) {
                            setPagerLoader(true);
                            getMemoizedData(nextPageLink.current, userToken, lang).then((res) => {
                                nextPageLink.current = res.links?.next;
                                if (res?.data) {
                                    setOptionsList((pre) => [
                                        ...(pre || []),
                                        ...(getOptions?.sorter
                                            ? getOptions?.sorter(res?.data)
                                            : res?.data.map((item) => {
                                                  if (getOptions?.id) {
                                                      return { ...item, id: item[getOptions?.id] };
                                                  }
                                                  return item;
                                              }))
                                    ]);
                                    setPagerLoader(false);
                                }
                            });
                        }
                    }
                }}
                ref={modalElement}
                className={classNames(
                    css.formSelectMenuList,
                    classes.formSelectMenuList,
                    menuAlign === 'center' ? css.menuCentered : '',
                    menuAlign === 'center' ? classes.menuCentered : '',
                    menuAlign === 'right' ? css.menuRight : '',
                    menuAlign === 'left' ? css.menuLeft : ''
                )}
                style={stylers.formSelectMenuList}
            >
                {dropdownSearch ? (
                    <FormInput
                        autofocus={true}
                        search={{}}
                        setFormData={(_, val) => setDropdownSearchValue(val)}
                        compStyle={{
                            wrapper: classNames(css.searchInputWrapper),
                            inner: css.searchInputInner,
                            input: css.searchInput
                        }}
                        // variation={'small'}
                        placeholder={
                            <T textName='DropdownMenu-search' defaultStr='Поиск' page='widget' translates={transl} group='form' />
                        }
                    />
                ) : null}
                {loading ? (
                    <div className={classNames(css.formSelectOptionAlert)}>
                        {t('DropdownMenu-loading', 'Загрузка', 'widget', transl, 'form')}
                        <CircleLoader className={css.loader} />
                    </div>
                ) : getOptions?.searchMinLength && searchValue?.length < getOptions?.searchMinLength ? (
                    <div className={classNames(css.formSelectOptionAlert)}>
                        {t('DropdownMenu-searchMinLength', 'Введите хотя бы', 'widget', transl, 'form')}{' '}
                        {getOptions?.searchMinLength}{' '}
                        {declOfNum(getOptions?.searchMinLength, [
                            t('DropdownMenu-searchMinLength-1', 'символ', 'widget', transl, 'form'),
                            t('DropdownMenu-searchMinLength-2', 'символа', 'widget', transl, 'form'),
                            t('DropdownMenu-searchMinLength-3', 'символов', 'widget', transl, 'form')
                        ])}
                    </div>
                ) : !optionsListFiltered?.length ? (
                    <div className={classNames(css.formSelectOptionAlert)}>
                        {searchValue?.length
                            ? t(
                                  'DropdownMenu-searchValue',
                                  'По Вашему запросу не было найдено ни одного совпадения',
                                  'widget',
                                  transl,
                                  'form'
                              )
                            : t('DropdownMenu-searchno-options', 'Параметры для выбора отсутствуют', 'widget', transl, 'form')}
                    </div>
                ) : optionsListFiltered ? (
                    optionsListFiltered.map((opt, ind) => {
                        const isSelected = validValue(multiple, val, opt);
                        if (multiple) {
                            const isDisabled = opt?.disabled || (limit && val && limit <= val?.length);
                            const props = {
                                onClick: () => {
                                    if (!isDisabled) {
                                        selectHandler(opt);
                                    }
                                },
                                className: classNames(
                                    css.formSelectOption,
                                    classes.formSelectOption,
                                    selectAll && ind === 0 ? css.formSelectOptionSelectAll : '',
                                    isDisabled ? css.formSelectOptionDisabled : ''
                                ),
                                style: stylers.formSelectOption
                            };
                            if (formatter) {
                                return (
                                    <React.Fragment key={`${attribute}_${getIdetify(opt)}_${ind}`}>
                                        {formatter(opt, props, true, searchValue)}
                                    </React.Fragment>
                                );
                            }
                            return (
                                <div {...props} key={`${attribute}_${getIdetify(opt)}_${ind}`}>
                                    <FormCheckbox
                                        key={`${attribute}_${getIdetify(opt)}_${ind}`}
                                        compStyle={{
                                            wrapper: classNames(
                                                classes.formSelectOptionCheckboxWrapper,
                                                (getIdetify(opt) === '<SELECT_ALL>' && allSelected) || isSelected
                                                    ? classes.formSelectOptionCheckboxWrapperSelected
                                                    : ''
                                            ),
                                            label: css.formSelectOptionCheckboxLabel
                                        }}
                                        value={(getIdetify(opt) === '<SELECT_ALL>' && allSelected) || isSelected ? '1' : '0'}
                                        title={{ body: <OptionTitle option={opt} searchValue={searchValue} /> }}
                                    />
                                </div>
                            );
                        }
                        const props = {
                            onClick: () => {
                                if (!opt?.disabled) {
                                    selectHandler(opt);
                                }
                            },
                            className: classNames(
                                css.formSelectOption,
                                classes.formSelectOption,
                                opt?.disabled ? css.formSelectOptionDisabled : ''
                            ),
                            style: stylers.formSelectOption
                        };
                        if (formatter) {
                            return (
                                <React.Fragment key={`${attribute}_${getIdetify(opt)}_${ind}`}>
                                    {formatter(opt, props, true, searchValue)}
                                </React.Fragment>
                            );
                        }
                        return (
                            <div key={`${attribute}_${getIdetify(opt)}_${ind}`} {...props}>
                                {insertComponentInOptionBefore &&
                                    insertComponentInOptionBefore({ options: nativeOptionsList, value: opt })}
                                <OptionTitle option={opt} searchValue={searchValue} />
                            </div>
                        );
                    })
                ) : null}
                {pagerLoader ? (
                    <div className={classNames(css.formSelectOptionAlert, css.formSelectOptionAlertPager)}>
                        <CircleLoader size={30} className={css.loader} />
                    </div>
                ) : null}
            </div>
        </>
    );
};
export default DropdownMenu;
